import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { getAllUsersWalletSummary, formatETH, formatUSD, getTimeAgo } from '../services/walletAnalyticsService';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import '../components/AdminLayout.css';

const PAGE_SIZE = 12;

// Postgres NUMERIC/BIGINT arrive from supabase-js as strings. Coerce before any
// arithmetic or comparison, or sorting silently goes lexicographic.
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const time = (v) => (v ? new Date(v).getTime() : 0);

const fullDate = (v) =>
    v ? new Date(v).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : '';

// Relative for the recent past, an explicit date beyond that.
const smartDate = (v) => {
    if (!v) return '—';
    const days = (Date.now() - new Date(v).getTime()) / 86400000;
    return days < 30 ? getTimeAgo(v) : new Date(v).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

const isAdmin = (u) => num(u.role_id) === 4;
const statusOf = (u) => String(u.account_status || 'active').toLowerCase();
const isSuspended = (u) => statusOf(u) === 'banned' || statusOf(u) === 'blocked';

const STATUS_BADGE = {
    active:  'badge-success',
    banned:  'badge-danger',
    blocked: 'badge-warning',
};

const SORT_ACCESSORS = {
    name: (u) => (u.full_name || u.email || '').toLowerCase(),
    status: (u) => (isSuspended(u) ? 0 : 1),
    role: (u) => num(u.role_id),
    balance: (u) => num(u.fiat_balance_usd),
    activity: (u) => num(u.total_transactions),
    joined: (u) => time(u.created_at),
};

const TEXT_COLUMNS = ['name'];

const ROLE_FILTERS = [
    { value: 'all', label: 'All Users' },
    { value: 'user', label: 'Users' },
    { value: 'admin', label: 'Admins' },
];

function AdminUsers() {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sort, setSort] = useState({ key: 'joined', dir: 'desc' });
    const [page, setPage] = useState(1);

    const fetchUsers = useCallback(async () => {
        setError(null);
        try {
            const result = await getAllUsersWalletSummary();
            if (result.success) {
                setUsers(result.data || []);
            } else {
                // Fallback reads public.users directly. It returns `id`, while the
                // RPC returns `user_id` — normalise, or every row key is undefined
                // and View navigates to /admin/users/undefined.
                const { data, error: fallbackError } = await supabase
                    .from('users')
                    .select('*')
                    .order('created_at', { ascending: false });
                if (fallbackError) throw fallbackError;
                setUsers((data || []).map((d) => ({ ...d, user_id: d.id })));
                setError('Showing limited data — wallet balances are unavailable.');
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.message || 'Could not load users.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        const userRole = user?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') { navigate('/welcome'); return; }
        fetchUsers();
    }, [user, navigate, fetchUsers]);

    const visible = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        let list = users;

        if (selectedRole !== 'all') {
            // Match on "is this an admin", not on role_id === 1. The DB default is
            // 2 and the schema allows 1/2/3, so filtering by a single id silently
            // hid users created outside the signup form.
            list = list.filter((u) => (selectedRole === 'admin' ? isAdmin(u) : !isAdmin(u)));
        }
        if (statusFilter !== 'all') {
            list = list.filter((u) => (statusFilter === 'suspended' ? isSuspended(u) : !isSuspended(u)));
        }
        if (term) {
            list = list.filter((u) =>
                u.email?.toLowerCase().includes(term) ||
                u.full_name?.toLowerCase().includes(term) ||
                u.wallet_address?.toLowerCase().includes(term) ||
                u.referral_code?.toLowerCase().includes(term));
        }

        const accessor = SORT_ACCESSORS[sort.key] || SORT_ACCESSORS.joined;
        const factor = sort.dir === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av < bv) return -1 * factor;
            if (av > bv) return 1 * factor;
            // Stable tiebreak so equal rows never jitter between renders.
            return time(b.created_at) - time(a.created_at);
        });
    }, [users, searchTerm, selectedRole, statusFilter, sort]);

    useEffect(() => { setPage(1); }, [searchTerm, selectedRole, statusFilter, sort]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const stats = useMemo(() => ({
        total: users.length,
        active: users.filter((u) => !isSuspended(u)).length,
        suspended: users.filter(isSuspended).length,
        admins: users.filter(isAdmin).length,
        totalUsd: users.reduce((s, u) => s + num(u.fiat_balance_usd), 0),
    }), [users]);

    const toggleSort = (key) => {
        setSort((prev) => (prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: TEXT_COLUMNS.includes(key) ? 'asc' : 'desc' }));
    };

    const handleRefresh = () => { setRefreshing(true); fetchUsers(); };

    const viewUserDetails = (u) => { navigate(`/admin/users/${u.user_id}`); };

    const handleExportCsv = () => {
        const headers = ['Name', 'Email', 'Status', 'Role', 'USD Balance', 'ETH Balance',
            'Transactions', 'Last Activity', 'Joined', 'Wallet Address', 'Referral Code'];
        const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = visible.map((u) => [
            u.full_name || '',
            u.email || '',
            statusOf(u),
            isAdmin(u) ? 'Admin' : 'User',
            num(u.fiat_balance_usd).toFixed(2),
            num(u.crypto_balance_eth).toFixed(6),
            num(u.total_transactions),
            u.last_transaction_date || '',
            u.created_at || '',
            u.wallet_address || '',
            u.referral_code || '',
        ].map(cell).join(','));
        const csv = [headers.map(cell).join(','), ...rows].join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `sigma-bank-users-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const StatCard = ({ title, value, sublabel, icon, colorClass }) => (
        <div className="stat-card">
            <div className={`stat-icon ${colorClass}`}><i className={`bx ${icon}`}></i></div>
            <div className="stat-content">
                <div className="stat-label">{title}</div>
                <div className="stat-value">{value}</div>
                {sublabel && <div className="stat-change">{sublabel}</div>}
            </div>
        </div>
    );

    const SortableTh = ({ label, sortKey, className = '', title }) => {
        const active = sort.key === sortKey;
        const icon = active ? (sort.dir === 'desc' ? 'bx-chevron-down' : 'bx-chevron-up') : 'bx-sort-alt-2';
        return (
            <th
                className={`sortable ${className}${active ? ' sorted' : ''}`}
                onClick={() => toggleSort(sortKey)}
                title={title || `Sort by ${label}`}
                aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
            >
                {label}
                <i className={`bx sort-icon ${icon}`}></i>
            </th>
        );
    };

    if (loading) {
        return <AdminLayout><Loader /></AdminLayout>;
    }

    return (
        <AdminLayout>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-user-circle"></i>
                        User Management
                    </h1>
                    <p className="page-subtitle">View and manage all platform users</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="btn btn-secondary" onClick={handleExportCsv}
                            disabled={visible.length === 0} title="Download the current view as CSV">
                        <i className="bx bx-download"></i>
                        Export CSV
                    </button>
                    <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                        <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`}></i>
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-danger">
                    <span className="admin-alert-message">
                        <i className="bx bx-error-circle"></i>
                        {error}
                    </span>
                    <button className="btn btn-sm btn-secondary" onClick={handleRefresh}>Retry</button>
                </div>
            )}

            <div className="stats-grid stats-grid-compact">
                <StatCard title="Total Users" value={stats.total.toLocaleString()} icon="bx-group" colorClass="primary" />
                <StatCard title="Active" value={stats.active.toLocaleString()}
                          sublabel={stats.total ? `${Math.round((stats.active / stats.total) * 100)}% of users` : '—'}
                          icon="bx-check-shield" colorClass="success" />
                <StatCard title="Suspended" value={stats.suspended.toLocaleString()}
                          sublabel={stats.suspended ? 'banned or blocked' : 'none'}
                          icon="bx-block" colorClass="danger" />
                <StatCard title="Admins" value={stats.admins.toLocaleString()} icon="bx-shield" colorClass="warning" />
                <StatCard title="Total Held" value={formatUSD(stats.totalUsd)}
                          sublabel="spendable across all users" icon="bx-wallet" colorClass="info" />
            </div>

            <div className="admin-card">
                <div className="card-header card-header-wrap">
                    <h3 className="card-title">
                        <i className="bx bx-list-ul"></i>
                        Users ({visible.length})
                    </h3>
                    <div className="card-toolbar">
                        <div className="card-search">
                            <i className="bx bx-search"></i>
                            <input
                                type="text"
                                placeholder="Search users…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-input"
                                aria-label="Search users"
                            />
                        </div>
                        {ROLE_FILTERS.map(({ value, label }) => (
                            <button
                                key={value}
                                className={`btn btn-sm ${selectedRole === value ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setSelectedRole(value)}
                                aria-pressed={selectedRole === value}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            className={`btn btn-sm ${statusFilter === 'suspended' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setStatusFilter((s) => (s === 'suspended' ? 'all' : 'suspended'))}
                            aria-pressed={statusFilter === 'suspended'}
                            title="Show only banned or blocked accounts"
                        >
                            Suspended
                        </button>
                    </div>
                </div>

                {visible.length === 0 ? (
                    <div className="admin-empty">
                        <i className={`bx ${searchTerm.trim() ? 'bx-search-alt' : 'bx-user-x'}`}></i>
                        {searchTerm.trim() ? (
                            <>
                                <p>No users match “{searchTerm.trim()}”</p>
                                <button className="btn btn-sm btn-secondary" onClick={() => setSearchTerm('')}>
                                    Clear search
                                </button>
                            </>
                        ) : (
                            <p>{error ? 'User data unavailable.' : 'No users found'}</p>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <SortableTh label="User" sortKey="name" />
                                        <SortableTh label="Status" sortKey="status" />
                                        <SortableTh label="Role" sortKey="role" />
                                        <SortableTh label="Balance" sortKey="balance" className="num"
                                                    title="Spendable USD balance; on-chain ETH beneath" />
                                        <SortableTh label="Activity" sortKey="activity" className="num"
                                                    title="Total transactions, and when the most recent one happened" />
                                        <SortableTh label="Joined" sortKey="joined" />
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((u) => {
                                        const status = statusOf(u);
                                        const name = u.full_name || u.email || '?';
                                        const txCount = num(u.total_transactions);
                                        return (
                                            <tr key={u.user_id}>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-avatar-small">
                                                            {name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-cell-text">
                                                            <button
                                                                className="link-button user-cell-name"
                                                                onClick={() => viewUserDetails(u)}
                                                                title={u.full_name || undefined}
                                                            >
                                                                {u.full_name || 'N/A'}
                                                            </button>
                                                            <div className="user-cell-sub" title={u.email}>{u.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${STATUS_BADGE[status] || 'badge-neutral'}`}>
                                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${isAdmin(u) ? 'badge-danger' : 'badge-neutral'}`}>
                                                        {isAdmin(u) ? 'Admin' : 'User'}
                                                    </span>
                                                </td>
                                                <td className="num">
                                                    {formatUSD(u.fiat_balance_usd)}
                                                    <div className="user-cell-sub">{formatETH(u.crypto_balance_eth)} ETH</div>
                                                </td>
                                                <td className="num">
                                                    {txCount}
                                                    <div className="user-cell-sub" title={fullDate(u.last_transaction_date)}>
                                                        {txCount > 0 ? smartDate(u.last_transaction_date) : 'no activity'}
                                                    </div>
                                                </td>
                                                <td title={fullDate(u.created_at)}>{smartDate(u.created_at)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => viewUserDetails(u)}
                                                        title="View this user's profile"
                                                    >
                                                        <i className="bx bx-show"></i>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="admin-pagination">
                                <span className="pagination-info">
                                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                                    {Math.min(currentPage * PAGE_SIZE, visible.length)} of {visible.length} users
                                </span>
                                <div className="pagination-controls">
                                    <button className="btn btn-sm btn-secondary"
                                            onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>
                                        <i className="bx bx-chevron-left"></i>
                                        Prev
                                    </button>
                                    <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                                    <button className="btn btn-sm btn-secondary"
                                            onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}>
                                        Next
                                        <i className="bx bx-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminUsers;
